import { Tabs as ChakraTabs, createSlotRecipeContext } from '@chakra-ui/react';
import * as React from 'react';

import { Badge } from '../badge';
import { tabsColorPalette, tabsVariant } from './tabs.kds';

const { withProvider, withContext } = createSlotRecipeContext({ key: 'tabs' });

/** KDS line tabs — `variant` and `colorPalette` are fixed, not configurable. */
export type TabsRootProps = Omit<ChakraTabs.RootProps, 'variant' | 'colorPalette'>;
export type TabsListProps = ChakraTabs.ListProps;
export type TabsContentProps = ChakraTabs.ContentProps;

const TabsRootInner = withProvider(ChakraTabs.Root, 'root') as typeof ChakraTabs.Root;

const TabsRoot = React.forwardRef<HTMLDivElement, TabsRootProps>(function TabsRoot(props, ref) {
  return <TabsRootInner ref={ref} colorPalette={tabsColorPalette} variant={tabsVariant} {...props} />;
});
TabsRoot.displayName = 'TabsRoot';

const TabsList = withContext(ChakraTabs.List, 'list') as typeof ChakraTabs.List;
TabsList.displayName = 'TabsList';

export interface TabsTriggerProps extends ChakraTabs.TriggerProps {
  /** Count badge beside the label. */
  badge?: React.ReactNode;
}

const TabsTriggerInner = withContext(ChakraTabs.Trigger, 'trigger') as typeof ChakraTabs.Trigger;

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(function TabsTrigger(props, ref) {
  const { badge, children, disabled, ...rest } = props;

  return (
    <TabsTriggerInner ref={ref} disabled={disabled} {...rest}>
      {children}
      {badge != null && <Badge disabled={disabled}>{badge}</Badge>}
    </TabsTriggerInner>
  );
});
TabsTrigger.displayName = 'TabsTrigger';

const TabsContent = withContext(ChakraTabs.Content, 'content') as typeof ChakraTabs.Content;
TabsContent.displayName = 'TabsContent';

export { TabsContent, TabsList, TabsRoot, TabsTrigger };

export const Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
};
